import { Editor, TLShapeId } from "tldraw";
import { AgentAction } from "../../../shared/types/AgentAction";
import { Streaming } from "../../../shared/types/Streaming";
import type { AgentCursorState } from "../../agent/managers/AgentCursorManager";

/**
 * Extracts page coordinates from a streaming agent action.
 * Returns null for non-spatial actions (cursor stays at last known position).
 */
export function extractCursorPositionFromAction(
  action: Streaming<AgentAction>,
  editor: Editor,
): { x: number; y: number } | null {
  switch (action._type) {
    case "create": {
      const shape = (action as any).shape;
      if (!shape) return null;
      // Shapes discriminate on `_type`, not `type` — reading `type` here always
      // saw `undefined`, so arrows and lines fell through to the `x`/`y` branch
      // below, which they do not have (they carry `x1/y1/x2/y2`) and returned
      // null. Every arrow and every figure edge therefore contributed nothing
      // to the cursor OR to the viewport-follow bounding box.
      if (shape._type === "arrow" || shape._type === "line") {
        // For arrow/line, use the endpoint (x2, y2) if available
        if (shape.x2 != null && shape.y2 != null) {
          return { x: shape.x2, y: shape.y2 };
        }
        // `line` may be given as x1/y1 only, or as a points array (both are
        // optional in the shape schema) — fall back rather than give up.
        if (shape.x1 != null && shape.y1 != null) {
          return { x: shape.x1, y: shape.y1 };
        }
        return lastPointOf(shape.points);
      }
      // For geo/text/note/etc, use origin
      if (shape.x != null && shape.y != null) {
        return { x: shape.x, y: shape.y };
      }
      return null;
    }

    case "move": {
      const a = action as any;
      if (a.x != null && a.y != null) {
        return { x: a.x, y: a.y };
      }
      return null;
    }

    // `highlight` is the marker-pen sibling of `pen` (same `points` payload) and
    // is a first-class ops action — annotating the student's work is one of the
    // most common things the notepad agent does. Omitting it here meant a turn
    // that only highlighted produced no position at all, so the camera never
    // followed the agent to the thing it was pointing at.
    case "pen":
    case "highlight": {
      const a = action as any;
      return lastPointOf(a.points);
    }

    // A `gesture` is the tutor pointing and saying "look at this", so it is the
    // action the camera should MOST want to follow — and it is the one type
    // that carries its target's bounds directly rather than a shape id, because
    // it never becomes a shape (it renders as an overlay). Without this case it
    // fell through to `default: null` and the tutor pointed at something the
    // student could not see.
    case "gesture": {
      const a = action as any;
      if (a.x == null || a.y == null) return null;
      return { x: a.x + (a.w ?? 0) / 2, y: a.y + (a.h ?? 0) / 2 };
    }

    case "delete":
    case "label": {
      const a = action as any;
      if (!a.shapeId) return null;
      return getShapeBoundsCenter(editor, a.shapeId);
    }

    case "update": {
      const a = action as any;
      const shapeId = a.update?.shapeId;
      if (!shapeId) return null;
      return getShapeBoundsCenter(editor, shapeId);
    }

    // Non-spatial actions: cursor stays at last known position
    case "message":
    case "unknown":
    case "countryInfo":
    default:
      return null;
  }
}

/**
 * Maps an action _type to a cursor display state.
 */
export function getAgentCursorStateFromAction(
  action: Streaming<AgentAction>,
): AgentCursorState {
  switch (action._type) {
    case "create":
      return "creating";
    case "pen":
      return "drawing";
    case "delete":
      return "deleting";
    case "move":
      return "moving";
    case "label":
    case "update":
      return "editing";
    default:
      return "generating";
  }
}

/** Last point of a `{x, y}[]` payload (pen / highlight strokes, line points). */
function lastPointOf(points: unknown): { x: number; y: number } | null {
  if (!Array.isArray(points) || points.length === 0) return null;
  const last = points[points.length - 1];
  if (!last || last.x == null || last.y == null) return null;
  return { x: last.x, y: last.y };
}

function getShapeBoundsCenter(
  editor: Editor,
  shapeId: string,
): { x: number; y: number } | null {
  const bounds = editor.getShapePageBounds(`shape:${shapeId}` as TLShapeId);
  if (!bounds) return null;
  return { x: bounds.center.x, y: bounds.center.y };
}
